import moment from "moment";
import { ReadingContextInterface } from "context/initialState/Readings";
import { MeasurementProp } from "components/shared/Timeline/Timeline";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { READING_TYPES } from "helpers/appConstants";
import Session from "helpers/session";
import API from "api";

declare global {
  interface Window {
    logoutTimer?: ReturnType<typeof setTimeout>;
  }
}

export const usStates = [
  "AL",
  "AK",
  "AS",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "DC",
  "FM",
  "FL",
  "GA",
  "GU",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MH",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "MP",
  "OH",
  "OK",
  "OR",
  "PW",
  "PA",
  "PR",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VI",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY"
];

export const dayArr = ["Day"];

export const monthArr = [
  "Month",
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December"
];
export const monthTranslate = (num: string): string => {
  switch (num) {
    case "01":
      return "January";
    case "02":
      return "February";
    case "03":
      return "March";
    case "04":
      return "April";
    case "05":
      return "May";
    case "06":
      return "June";
    case "07":
      return "July";
    case "08":
      return "August";
    case "09":
      return "September";
    case "10":
      return "October";
    case "11":
      return "November";
    case "12":
      return "December";
    case "January":
      return "01";
    case "February":
      return "02";
    case "March":
      return "03";
    case "April":
      return "04";
    case "May":
      return "05";
    case "June":
      return "06";
    case "July":
      return "07";
    case "August":
      return "08";
    case "September":
      return "09";
    case "October":
      return "10";
    case "November":
      return "11";
    case "December":
      return "12";
    default:
      return num;
  }
};

const dayValue = 31;
const yearValue = new Date().getFullYear();

export const yearArr = ["Year"];

for (let i = dayValue; i >= 1; i--) {
  dayArr.push(`${i}`);
}
export const dateTranslate = (num: string): string => {
  switch (num) {
    case "01":
      return "1";
    case "02":
      return "2";
    case "03":
      return "3";
    case "04":
      return "4";
    case "05":
      return "5";
    case "06":
      return "6";
    case "07":
      return "7";
    case "08":
      return "8";
    case "09":
      return "9";
    default:
      return num;
  }
};
for (let i = yearValue; i >= 1900; i--) {
  yearArr.push(`${i}`);
}

export const toLocalTime = (timeStamp: string | undefined): string => {
  if (timeStamp) {
    return moment(timeStamp).local().format("MM/DD/YYYY HH:mm");
  } else {
    return "-";
  }
};

export const associateReadingType = (readingType: string): string => {
  const {
    bloodPressure,
    heartRate,
    bloodGlucose,
    spirometry,
    temperature,
    weight,
    oxygenSaturation,
    respirationRate,
    memberNote,
    activity,
    sleep
  } = READING_TYPES;
  switch (readingType) {
    case bloodPressure.abbreviation:
      return bloodPressure.display;
    case bloodPressure.display:
      return bloodPressure.abbreviation;
    case bloodGlucose.abbreviation:
      return bloodGlucose.display;
    case bloodGlucose.display:
      return bloodGlucose.abbreviation;
    case heartRate.abbreviation:
      return heartRate.display;
    case heartRate.display:
      return heartRate.abbreviation;
    case oxygenSaturation.abbreviation:
      return oxygenSaturation.display;
    case oxygenSaturation.display:
      return oxygenSaturation.abbreviation;
    case temperature.abbreviation:
      return temperature.display;
    case temperature.display:
      return temperature.abbreviation;
    case weight.abbreviation:
      return weight.display;
    case weight.display:
      return weight.abbreviation;
    case respirationRate.abbreviation:
      return respirationRate.display;
    case respirationRate.display:
      return respirationRate.abbreviation;
    case spirometry.abbreviation:
      return spirometry.display;
    case spirometry.display:
      return spirometry.abbreviation;
    case activity.abbreviation:
      return activity.display;
    case sleep.abbreviation:
      return sleep.display;
    case sleep.display:
      return sleep.abbreviation;
    case memberNote.abbreviation:
      return "Member Note";
    case "Member Note":
      return memberNote.abbreviation;
    default:
      return "";
  }
};

export const formatSpirometry = (device: ReadingContextInterface) => {
  const { metrics } = device;

  let spirometryReading = {
    FVC: "",
    FEV1: "",
    FEV1_FVC: "",
    FEV6: "",
    FEF2575: "",
    PEF: "",
    PEF60: "",
    Points: [],
    FVCExGraph: [],
    Medication: "",
    Status: "",
    device: ""
  };
  if (metrics?.description === "Spirobank Smart") {
    const readingData = JSON.parse(
      metrics.spirometry ? metrics.spirometry : ""
    );
    spirometryReading = {
      FVC: (readingData.Summary.FVC / 100).toFixed(2),
      FEV1: (readingData.Summary.FEV1 / 100).toFixed(2),
      // TODO: Need to clarify what goes here
      FEV1_FVC: "",
      FEV6: "",
      FEF2575: "",

      PEF: (readingData.Summary.PEF / 100).toFixed(2),
      PEF60: ((readingData.Summary.PEF / 100) * 60).toFixed(2),
      Points: readingData.Points,
      FVCExGraph: readingData.Summary.FVCExGraph,
      Medication: "Pre",
      Status: ".",
      device: "Spirobank Smart"
    };
  } else if (metrics?.description === "Vitalograph 40372") {
    spirometryReading = {
      FVC: `${metrics?.fvc?.value ? metrics.fvc.value : "-"}`,
      FEV1: `${metrics?.fev1?.value ? metrics.fev1.value : "-"}`,
      // TODO: Need to clarify what goes here
      FEV1_FVC: "",
      FEV6: "",
      FEF2575: "",

      PEF: `${
        metrics?.pef?.value ? (metrics.pef.value / 100).toFixed(2) : "-"
      }`,
      PEF60: `${
        metrics?.pef?.value ? ((metrics.pef.value / 100) * 60).toFixed(2) : "-"
      }`,
      Points: [],
      FVCExGraph: [],
      Medication: "Pre",
      Status: ".",
      device: "Vitalograph 40372"
    };
  }
  return spirometryReading;
};

export const formatReading = (
  rawReading: ReadingContextInterface,
  tempUnits?: string
): MeasurementProp => {
  const {
    bloodPressure,
    bloodGlucose,
    spirometry,
    temperature,
    weight,
    oxygenSaturation,
    heartRate,
    activity,
    sleep
  } = READING_TYPES;

  const { metrics } = rawReading;
  const spir = formatSpirometry(rawReading);
  const FEV1 = Number(spir.FEV1);
  const FVC = Number(spir.FVC);
  switch (rawReading.type) {
    case heartRate.abbreviation:
      return {
        id: rawReading.id,
        type: "Heart Rate",
        value: `${metrics?.pulse} bpm`,
        date: moment(rawReading.measuredAt).format("MMM DD, HH:mm"),
        year: moment(rawReading.measuredAt).format("YYYY")
      };
    case bloodPressure.abbreviation:
      return {
        id: rawReading.id,
        type: bloodPressure.display,
        value: `${metrics?.systolic}/${metrics?.diastolic} mmHg\n${metrics?.pulse} bpm`,
        date: moment(rawReading.measuredAt).format("MMM DD, HH:mm"),
        year: moment(rawReading.measuredAt).format("YYYY")
      };
    case bloodGlucose.abbreviation:
      return {
        id: rawReading.id,
        type: bloodGlucose.display,
        value: `${metrics?.glucose} mg/dL`,
        date: moment(rawReading.measuredAt).format("MMM DD, HH:mm"),
        year: moment(rawReading.measuredAt).format("YYYY")
      };
    case oxygenSaturation.abbreviation:
      return {
        id: rawReading.id,
        type: oxygenSaturation.display,
        value: metrics?.pulse
          ? `${metrics.spo2}%\n${metrics.pulse} bpm`
          : `${metrics?.spo2}%`,
        date: moment(rawReading.measuredAt).format("MMM DD, HH:mm"),
        year: moment(rawReading.measuredAt).format("YYYY")
      };
    case temperature.abbreviation:
      if (tempUnits === "us" && metrics) {
        metrics.units = "F";
        metrics.temp = convertToFahrenheit(metrics.temp || 1);
      }
      return {
        id: rawReading.id,
        type: "Temperature",
        value: `${metrics?.temp} ˚${metrics?.units}`,
        date: moment(rawReading.measuredAt).format("MMM DD, HH:mm"),
        year: moment(rawReading.measuredAt).format("YYYY")
      };
    case weight.abbreviation:
      return {
        id: rawReading.id,
        type: "Weight",
        value: `${metrics?.weight} lbs`,
        date: moment(rawReading.measuredAt).format("MMM DD, HH:mm"),
        year: moment(rawReading.measuredAt).format("YYYY")
      };
    case spirometry.abbreviation:
      return {
        id: rawReading.id,
        type: spirometry.display,
        value:
          formatSpirometry(rawReading).device === "Spirobank Smart"
            ? `FEV1/FVC ${((FEV1 / FVC) * 100).toFixed(1)} %`
            : `FEV1 ${FEV1}`,
        date: moment(rawReading.measuredAt).format("MMM DD, HH:mm"),
        year: moment(rawReading.measuredAt).format("YYYY")
      };
    case activity.abbreviation:
      return {
        id: rawReading.id,
        type: "Activity",
        value: `${metrics?.steps} steps`,
        date: moment(rawReading.measuredAt).format("MMM DD, HH:mm"),
        year: moment(rawReading.measuredAt).format("YYYY")
      };
    case sleep.abbreviation:
      return {
        id: rawReading.id,
        type: "Sleep",
        value: `${metrics?.sleep} hrs`,
        date: moment(rawReading.measuredAt).format("MMM DD, HH:mm"),
        year: moment(rawReading.measuredAt).format("YYYY")
      };
    default:
      return { id: "", type: "", value: "", date: "", year: "" };
  }
};

export const idIncrement = (id: string) => {
  return id.slice(0, 19) + (parseInt(id.slice(19, 24), 16) + 1).toString(16);
};

export const idDecrement = (id: string) => {
  return id.slice(0, 19) + (parseInt(id.slice(19, 24), 16) + 1).toString(16);
};

export const snakeToCamel = (str: string) => {
  return str.replace(/[^a-zA-Z0-9]+(.)/g, (m, chr) => chr.toUpperCase());
};

export const camelFromLabel = (label: string): string =>
         label.replace(/\s+/g, "").replace(/[^a-zA-Z0-9]/g, "").toLowerCase();

export const convertToCelcius = (num: number) => {
  if (num < 70) {
    return num;
  } else {
    return Math.round((num - 32) * (5 / 9) * 10) / 10;
  }
};

export const convertToFahrenheit = (num: number) => {
  if (num > 70) {
    return num;
  } else {
    return Math.round((num * (9 / 5) + 32) * 10) / 10;
  }
};

export const tempConversion = (num: number, units: string | undefined) => {
  if (units === "metric") {
    return convertToCelcius(num);
  } else {
    return convertToFahrenheit(num);
  }
};

export const exportGenerator = (
  option: string,
  containerId: any,
  name: string
) => {
  // Possibly a switch statement instead of an if statement.
  if (option === "toCSV") {
    //  functionToCSV();
  } else if (option === "toPDF") {
    const input = document.getElementById(containerId)!;
    html2canvas(input).then((canvas) => {
      const imgData = canvas.toDataURL("img/png");
      const pdf = new jsPDF();
      pdf.addImage(imgData, "JPEG", 15, 12, 180, 180);
      pdf.save(`${name}.pdf`);
    });
  }
};

export const exportLink = (
  fileContent: string,
  fileName: string,
  mimeType: string
) => {
  const blob = new Blob([fileContent], { type: mimeType });
  const target = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = target;
  a.download = fileName;
  a.click();
  setTimeout(() => URL.revokeObjectURL(target), 150);
  return a;
};

export const isValidEmail = (email: string) => {
  const match = String(email)
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    );
  if (match !== null || "") {
    return true;
  } else {
    return false;
  }
};

export const AddEncounterEvent = (
//   coordinatorId: string,
//   coordinatorName: string,
//   memberId: string,
//   memberFirstName: string,
//   memberLastName: string,
//   duration: Number,
//   escalatedDuration: Number,
  actionType:
    | "note"
    | "message"
    | "intervention"
    | "readings review"
    | "profile change",
  note: string
) => {
  const stamp = moment(new Date()).format("MMM DD, YYYY HH:mm");
  const actionBody = {
    timestamp: stamp,
    type: actionType,
    noteContent: note
  };
  return actionBody;
//   return {
//     coordinatorId,
//     coordinatorName,
//     memberId,
//     memberName,
//     duration,
//     escalatedDuration,
//     actionBody
//   };
};

// TODO: This is a temporary means to address naming conventions as they come in from the
// notifications context and member context. Will look to simplify the context in the future.
export const toLastFirstName = (name: string) => {
  if (typeof name !== "string") {
    return "";
  }
  const match = name.match(/^(.*)\s+(\S+)\s*$/);
  return match
    ? match[2].toUpperCase() +
        ", " +
        match[1].replace(match[1][0], match[1][0].toUpperCase())
    : name;
};

export const formatTableDisplayValue = (
  displayProperty: any,
  displayText: string
) => (displayProperty ? displayProperty : displayText);

export const isWholeNumber = (value: string): boolean => {
  return value === "" || (/^\d+$/.test(value) && parseInt(value) >= 1);
};

export const GROUP_KEY_MAP: Record<string, string> = {
  bp: "bloodPressure",
  bgl: "bloodGlucose",
  hrt: "heartRate",
  spo2: "oxygenSaturation",
  spir: "spirometry",
  temp: "temperature",
  wt: "weight",
  activity: "activity",
  sleep: "sleep"
};

export const TYPE_TO_GROUP: Record<string, string> = {
  "Blood Pressure": "bp",
  "Blood Glucose": "bgl",
  "Heart Rate": "hrt",
  "Oxygen Saturation": "spo2",
  "Spirometry": "spir",
  "Temperature": "temp",
  "Weight": "wt",
  "Activity": "activity",
  "Sleep": "sleep"
};

export const MEASUREMENT_KEY_MAP: Record<string, string> = {
  "activity": "activity",
  "blood-glucose": "bgl",
  "blood-pressure": "bp",
  "heart-rate": "hrt",
  "oxygen-saturation": "spo2",
  "spirometry": "spirometry",
  "temperature": "temp",
  "weight": "wt",
  "sleep": "sleep",
};

export const ABBREV_TO_DISPLAY: Record<string, string> = {
  "bp": "Blood Pressure",
  "bgl": "Blood Glucose",
  "hrt": "Heart Rate",
  "spo2": "Oxygen Saturation",
  "spir": "Spirometry",
  "temp": "Temperature",
  "wt": "Weight",
  "activity": "Activity",
  "sleep": "Sleep"
};

export const SPIROMETRY_KEY_MAP: Record<string, string> = {
  fev1: "FEV1",
  fvc: "FVC",
  fev1fvc: "FEV1_FVC",
  pef: "PEF",
};

export const csvEscape = (value: unknown): string => {
  if (value == null) return "";
  const stringValue = String(value);
  if (/[",\n\r]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

export const parseReadingDateToISO = (dateString: string): string => {
  if (!dateString?.trim()) return "";
  const parsed = moment.utc(dateString.trim(), "MMM DD YYYY HH:mm", true);
  return parsed.isValid() ? parsed.toISOString() : "";
};

export const parseNotificationDateToISO = (dateString: string): string => {
  if (!dateString?.trim()) return "";
  const parsed = moment.utc(dateString.trim());
  return parsed.isValid() ? parsed.toISOString() : "";
};

export const parseDateToISO = (dateString: string, dateKind: string): string => {
  let format: string | undefined;
  let strict = true;

  switch (dateKind) {
    case "encounter":
      format = "MM/DD/YY hh:mm A";
      break;
    case "reading":
      format = "MMM DD YYYY HH:mm";
      break;
    case "notification":
      format = undefined;
      strict = false;
      break;
  }

  if (!dateString?.trim()) return "";
  const parsed = format
    ? moment.utc(dateString.trim(), format, strict)
    : moment.utc(dateString.trim());
  return parsed.isValid() ? parsed.toISOString() : "";
};

export const saveActiveEncounter = (
  member: any,
  encounterTimerDispatch: any,
  settingsDispatch: any,
  duration: number,
  useKeepalive = false
) => {
  const rawEncounter = window.sessionStorage.getItem("encounter");
  if (!rawEncounter) return;

  const encounterEvent = AddEncounterEvent("intervention", "Patient closed");
  Session.updateSessionEncounter(rawEncounter, encounterEvent, duration.toString());

  let encounter;
  try {
    encounter = JSON.parse(Session.decrypt(window.sessionStorage.getItem("encounter") || ""));
  } catch (err) {
    console.error("PARSE FAILED:", err);
    encounter = null;
  }

  if (encounter && encounter.actions && encounter.actions.length !== 0) {
    try {
      if (useKeepalive) {
        API.kore.encounters.addKeepalive(Session.crackCookieToken("coordinator"), encounter);
      } else {
        API.kore.encounters.add(Session.crackCookieToken("coordinator"), encounter);
      }
    } catch (err) {
      console.error("Failed to save encounter", err);
    }
  }

  encounterTimerDispatch({ type: "RESET_ENCOUNTER_TIMER" });
  window.sessionStorage.removeItem("encounter");
  settingsDispatch({ type: "CLEAR_MEMBER_SETTINGS" });
};